import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { Button, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { AddLinkButton } from './AddLinkButton';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { ManageLinkModal } from './ManageLinkModal';

export type LinkType = Readonly<{
	link: string;
	text?: string;
}>;

const linksJsonSchema = z.object({
	values: z.array(
		z.object({
			link: z.string(),
			text: z.string().optional(),
		}),
	),
});

export const isLinksJson = (links: unknown): links is { values: LinkType[] } => {
	return linksJsonSchema.safeParse(links).success;
};

const oldLinksJsonSchema = z.object({
	values: z.array(z.string()),
});

export const isOldLinksJson = (links: unknown): links is { values: string[] } => {
	return oldLinksJsonSchema.safeParse(links).success;
};

type LinksFormInputProps = Readonly<{
	value?: LinkType[] | null;
	isLoading: boolean;
	hideAdd?: boolean;
	onChange: (value: LinkType[]) => void;
	maxNumberOfLinks?: number;
	size: 'medium' | 'small';
}>;

export const LinksFormInput: FC<LinksFormInputProps> = ({ value, isLoading, hideAdd, onChange, maxNumberOfLinks, size }) => {
	const theme = useTheme();
	const [links, setLinks] = useState<LinkType[]>([]);
	const [shouldUpdate, setShouldUpdate] = useState(false);
	const [manageLinkModal, setManageLinkModal] = useState<ReactNode>(null);

	useEffect(() => {
		if (!isLoading) {
			setLinks(value ?? []);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	const update = () => {
		const linksWithoutProtocol = links.map(({ link, text }) => ({ link: link.replace(/(http)s?:\/\//, '').trim(), text }));
		onChange(linksWithoutProtocol);
	};

	useEffect(() => {
		if (shouldUpdate) {
			update();
			setShouldUpdate(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldUpdate, value]);

	const showAddButton = maxNumberOfLinks == null || links.length < maxNumberOfLinks;

	if (isLoading) {
		return (
			<Stack sx={{ position: 'relative' }}>
				<Typography sx={{ color: theme.palette.text.disabled, padding: '4px 24px 5px 0px' }}>
					<FormattedMessage id={'common.loading'} />
				</Typography>
				<LinearProgress
					sx={{
						position: 'absolute',
						bottom: 0,
						width: '100%',
						height: 4,
						borderTopRightRadius: 0,
						borderTopLeftRadius: 0,
					}}
				/>
			</Stack>
		);
	}

	return (
		<Stack direction={'column'} gap={size === 'medium' ? 1 : 0} alignItems={'flex-start'} width={'100%'}>
			{links.length > 0 && (
				<Stack width={'100%'} gap={size === 'medium' ? 1.5 : 0}>
					{links.map(({ link, text }, index) => (
						<Stack
							key={index}
							flexWrap={'nowrap'}
							direction={'row'}
							alignItems={'center'}
							gap={3}
							sx={{
								'&:not(:hover) .delete-link, &:not(:hover) .edit-link': {
									display: 'none',
								},
								height: 24,
							}}
						>
							<Link
								href={`https://${link}`}
								target={'_blank'}
								rel={'noreferrer'}
								color={theme.palette.grey[700]}
								variant={size === 'small' ? 'h6' : undefined}
								sx={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									flex: '0 1 auto',
								}}
							>
								{text || link}
							</Link>
							<Stack direction={'row'} gap={1}>
								<Button
									variant={'text'}
									size={'small'}
									color={'primary'}
									className={'edit-link'}
									sx={{ width: 24, height: 24, minWidth: 0, flexShrink: 0 }}
									onClick={() => {
										setManageLinkModal(
											<ManageLinkModal
												text={text}
												value={link}
												onChange={(value: LinkType) => {
													setShouldUpdate(true);
													setLinks((prev) => {
														const newState = [...prev];
														newState.splice(index, 1, value);
														return newState;
													});
												}}
												close={() => setManageLinkModal(null)}
											/>,
										);
									}}
								>
									<EditOutlined sx={{ width: 18, height: 18 }} htmlColor={theme.palette.grey[700]} />
								</Button>
								<Button
									variant={'text'}
									size={'small'}
									color={'primary'}
									className={'delete-link'}
									sx={{ width: 24, height: 24, minWidth: 0, flexShrink: 0 }}
									onClick={() => {
										setShouldUpdate(true);
										setLinks((prev) => {
											const newState = [...prev];
											newState.splice(index, 1);
											return newState;
										});
									}}
								>
									<DeleteOutlined sx={{ width: 18, height: 18 }} htmlColor={theme.palette.grey[700]} />
								</Button>
							</Stack>
						</Stack>
					))}
				</Stack>
			)}
			{showAddButton && (
				<AddLinkButton
					onChange={(value: LinkType) => {
						setShouldUpdate(true);
						setLinks((prev) => [...prev, value]);
					}}
					size={size}
					hideAdd={hideAdd}
				/>
			)}
			{manageLinkModal}
		</Stack>
	);
};
